"use client";

import * as React from "react";
import Link from "next/link";
import { Loader2, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/logo";
import { browserAuth } from "@/lib/auth-client";

export default function ForgotPasswordPage() {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Podaj email");
      return;
    }
    setLoading(true);
    try {
      await browserAuth.forgotPassword(email);
      // Zawsze pokazujemy "wysłano" — nie ujawniamy czy email istnieje
      // (account enumeration prevention, jak w naszym login flow).
      setSent(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Spróbuj ponownie";
      toast.error("Nie udało się wysłać emaila", { description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background p-4">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,oklch(0.92_0_0)/0.4_1px,transparent_1px),linear-gradient(to_bottom,oklch(0.92_0_0)/0.4_1px,transparent_1px)] bg-[size:48px_48px] dark:bg-[linear-gradient(to_right,oklch(0.27_0_0)/0.3_1px,transparent_1px),linear-gradient(to_bottom,oklch(0.27_0_0)/0.3_1px,transparent_1px)]"
      />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/60 backdrop-blur-sm">
          <CardContent className="space-y-6 p-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <Logo size="lg" showText={false} />
              <h1 className="text-2xl font-semibold tracking-tight">
                {sent ? "Sprawdź email" : "Reset hasła"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {sent
                  ? `Jeśli ${email} jest zarejestrowany, wysłaliśmy link do resetu hasła.`
                  : "Podaj email — wyślemy link do ustawienia nowego hasła."}
              </p>
            </div>

            {!sent && (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="ty@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      required
                      autoFocus
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} size="lg" className="w-full">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Wyślij link
                </Button>
              </form>
            )}

            <p className="text-center text-sm text-muted-foreground">
              <Link href="/login" className="font-medium text-primary hover:underline">
                ← Powrót do logowania
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
